import { createContext, useContext, useMemo, type ReactNode } from 'react';

/**
 * Shape of the client-side user context consumed by `<PermissionGate>` and
 * `<NavFilter>`. Permissions live in a `Set<string>` so lookups are O(1) —
 * a single admin nav render can easily trigger 20+ gate checks.
 *
 * Per `docs/RBAC-DESIGN-2026-04-21.md` §7: the login response will eventually
 * carry a compact `permissions` array (~40–60 strings for Owner, ~5–10 for
 * Walker). The host app is responsible for hydrating this context from that
 * payload on mount; the UI package stays transport-agnostic.
 */
export interface CurrentUser {
  readonly id: string;
  readonly roleSlug: string;
  readonly permissions: ReadonlySet<string>;
  readonly username?: string;
  readonly firstName?: string | null;
  readonly lastName?: string | null;
}

const CurrentUserContext = createContext<CurrentUser | null>(null);

export interface CurrentUserProviderProps {
  readonly value: CurrentUser | null;
  readonly children: ReactNode;
}

export function CurrentUserProvider({
  value,
  children,
}: CurrentUserProviderProps): JSX.Element {
  return (
    <CurrentUserContext.Provider value={value}>{children}</CurrentUserContext.Provider>
  );
}

const EMPTY_USER: CurrentUser = {
  id: '',
  roleSlug: '',
  permissions: new Set<string>(),
};

/**
 * Returns the active user. If no provider wraps the consumer, returns an
 * empty user with no permissions — this fails closed (gates hide content)
 * rather than open.
 */
export function useCurrentUser(): CurrentUser {
  const ctx = useContext(CurrentUserContext);
  return ctx ?? EMPTY_USER;
}

/**
 * Tiny helper for callers that want a concrete Set without knowing whether
 * the provider supplied one. Useful for memoised selectors that feed
 * `NavFilter` from outside the component tree.
 */
export function usePermissions(): ReadonlySet<string> {
  const user = useCurrentUser();
  return user.permissions;
}

/**
 * Build a `CurrentUser` from a plain permission array. Convenience for host
 * apps that receive `string[]` from the staff-login response and need to
 * freeze it into the Set the context expects.
 */
export function buildCurrentUser(args: {
  id: string;
  roleSlug: string;
  permissions: readonly string[];
  username?: string;
  firstName?: string | null;
  lastName?: string | null;
}): CurrentUser {
  return {
    id: args.id,
    roleSlug: args.roleSlug,
    permissions: new Set(args.permissions),
    username: args.username,
    firstName: args.firstName,
    lastName: args.lastName,
  };
}

/**
 * Memoised variant suitable for wrapping the provider. Re-builds the Set
 * only when the `permissions` array identity changes, so downstream
 * `useCurrentUser` consumers don't re-render on unrelated parent updates.
 */
export function useMemoCurrentUser(args: {
  id: string;
  roleSlug: string;
  permissions: readonly string[];
  username?: string;
  firstName?: string | null;
  lastName?: string | null;
}): CurrentUser {
  return useMemo(
    () => buildCurrentUser(args),
    [
      args.id,
      args.roleSlug,
      args.permissions,
      args.username,
      args.firstName,
      args.lastName,
    ],
  );
}
